import { useEffect } from "react";

// Legal body text is intentionally English-only (unlike the rest of the
// site) — it's sourced from a single reviewed draft, and machine-translating
// a legal document risks misstating it in ways nobody would catch. The page
// is still reachable under every language prefix (routing doesn't require
// translated content), it just always renders this same authoritative text.
const PrivacyPage = () => {
  useEffect(() => {
    document.title = "Privacy Policy | M A Galaxy International";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "How M A Galaxy International collects, uses, and protects personal data submitted through this website."
      );
    }
  }, []);

  return (
    <div data-testid="privacy-page">
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-navy text-white">
        <div className="container-custom max-w-3xl">
          <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">Legal</span>
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold mt-4 mb-4">Privacy Policy</h1>
          <p className="text-slate-400 text-sm">Last updated: 7 September 2026</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-custom max-w-3xl">
          <h2 className="font-outfit text-2xl font-bold text-navy mt-0 mb-4">1. Who We Are</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            This website (magalaxyintl.com) is operated by M A Galaxy International, comprising:
          </p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>
              <strong className="text-navy">M A Galaxy Int UK Ltd</strong> — Company Registration Number 13671542,
              VAT Number GB425010549, registered at 62 New Road, Newhall, Swadlincote, Derby, DE11 0TH, United Kingdom
            </li>
            <li>
              <strong className="text-navy">M A Galaxy International (Korea HQ)</strong> — 4385-23 Seodong-daero,
              Gongdo-eup, Anseong, Gyeonggi-do, South Korea
            </li>
          </ul>
          <p className="text-slate-600 leading-relaxed mb-4">
            For the purposes of UK and EU data protection law, M A Galaxy Int UK Ltd is the data controller for
            personal data collected through this website in relation to UK and European visitors.
          </p>
          <p className="text-slate-600 leading-relaxed mb-4">
            If you have any questions about this policy or how we handle your data, contact us at{" "}
            <a href="mailto:info@magalaxyintl.com" className="text-gold hover:underline">info@magalaxyintl.com</a> or{" "}
            <a href="tel:+447379339449" className="text-gold hover:underline"><span dir="ltr">+44 7379 339 449</span></a>.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">2. What Information We Collect</h2>
          <p className="text-slate-600 leading-relaxed mb-4">We collect information in the following ways:</p>
          <p className="text-slate-600 leading-relaxed mb-2"><strong className="text-navy">a) Information you provide directly</strong>, when you:</p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>Submit an enquiry form (Sell Panels, Buyer Portal request, or Korea Operations enquiry) — we collect your name, company name, phone number, email address, and details about your enquiry (e.g. panel quantity, brand, location, or message)</li>
            <li>Request access to our Trade Portal — we collect your name, company, country, phone number, email address, and volume requirements</li>
            <li>Submit an offer through the Trade Portal — we collect your offer price, quantity, and any message you provide</li>
            <li>Log in to admin functions (staff only) — access is password-protected and restricted to authorised personnel</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mb-2"><strong className="text-navy">b) Information collected automatically</strong>, when you browse the site:</p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>We use Google Analytics to understand how visitors use our website (pages visited, time spent, general location at country/city level, device and browser type). This uses cookies, as described in Section 6 below.</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mb-4">
            We do not knowingly collect any special category data (such as health information, religious beliefs, or similar) through this website.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">3. How We Use Your Information</h2>
          <p className="text-slate-600 leading-relaxed mb-4">We use the information we collect to:</p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>Respond to your enquiries and provide quotes for buying or selling solar panels</li>
            <li>Process and manage Trade Portal access requests and sealed offers</li>
            <li>Communicate with you about ongoing transactions or enquiries</li>
            <li>Understand and improve how our website is used</li>
            <li>Comply with legal and regulatory obligations (e.g. our obligations as a registered waste carrier)</li>
          </ul>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">4. Legal Basis for Processing (UK/EU Visitors)</h2>
          <p className="text-slate-600 leading-relaxed mb-4">Where UK GDPR or EU GDPR applies, we rely on the following legal bases:</p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li><strong className="text-navy">Legitimate interests</strong> — to respond to business enquiries and operate our Trade Portal, where this doesn't override your own rights and interests</li>
            <li><strong className="text-navy">Consent</strong> — for non-essential cookies such as Google Analytics (see Section 6)</li>
            <li><strong className="text-navy">Legal obligation</strong> — where we need to keep records for waste carrier compliance or other regulatory purposes</li>
          </ul>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">5. Who We Share Your Information With</h2>
          <p className="text-slate-600 leading-relaxed mb-4">We do not sell your personal data. We share information with:</p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>
              <strong className="text-navy">Service providers</strong> who help us operate the website and our business, including:
              <ul className="list-disc ps-6 mt-2 space-y-1">
                <li>Google Analytics (website usage analytics)</li>
                <li>Resend (email delivery for enquiry notifications)</li>
                <li>MongoDB Atlas (secure database hosting for enquiry and portal data)</li>
              </ul>
            </li>
            <li><strong className="text-navy">Professional advisers</strong> (e.g. legal or accounting), where necessary</li>
            <li><strong className="text-navy">Regulatory authorities</strong>, where required by law (e.g. in connection with our waste carrier registration)</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mb-4">We do not share your data with third parties for their own marketing purposes.</p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">6. Cookies</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            Our website uses cookies set by Google Analytics to help us understand website traffic and usage patterns.
            These are <strong className="text-navy">not essential</strong> to the website functioning, and are only
            set with your consent, given via the cookie banner shown when you first visit the site.
          </p>
          <p className="text-slate-600 leading-relaxed mb-4">
            You can withdraw or change your consent at any time using the "Cookie Settings" link in the footer of this website.
          </p>
          <p className="text-slate-600 leading-relaxed mb-4">
            You can also control cookies through your browser settings, though disabling all cookies may affect your experience on other websites.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">7. International Data Transfers</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            As our Korea office is a core part of our operations, some enquiry information (particularly relating to
            Korea Operations enquiries) may be accessible to staff based in South Korea. Where personal data is
            transferred outside the UK/EEA, we take steps to ensure it is protected to a standard consistent with
            UK/EU data protection law.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">8. Data Retention</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We retain enquiry and portal data for as long as necessary to respond to your enquiry, manage any ongoing
            business relationship, and comply with our legal obligations (including waste carrier record-keeping
            requirements). We periodically review and remove data that is no longer needed.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">9. Your Rights</h2>
          <p className="text-slate-600 leading-relaxed mb-4">If UK GDPR or EU GDPR applies to you, you have the right to:</p>
          <ul className="list-disc ps-6 space-y-2 text-slate-600 mb-4">
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data, where applicable</li>
            <li>Object to or restrict certain processing</li>
            <li>Request a copy of your data in a portable format</li>
            <li>Withdraw consent at any time (for cookies or other consent-based processing)</li>
          </ul>
          <p className="text-slate-600 leading-relaxed mb-4">
            To exercise any of these rights, contact us at{" "}
            <a href="mailto:info@magalaxyintl.com" className="text-gold hover:underline">info@magalaxyintl.com</a>.
            If you're not satisfied with our response, you have the right to complain to the UK Information
            Commissioner's Office (ICO) at{" "}
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">ico.org.uk</a>,
            or your local data protection authority if you're based in the EU.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">10. Security</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We take reasonable technical and organisational measures to protect the personal data we hold, including
            secure hosting, access controls on our admin systems, and password-protected access to the Trade Portal.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">11. Children's Privacy</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            This website is intended for business use and is not directed at children. We do not knowingly collect
            personal data from anyone under 18.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">12. Changes to This Policy</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We may update this policy from time to time. The "Last updated" date at the top will reflect the most
            recent changes. We encourage you to review this page periodically.
          </p>

          <h2 className="font-outfit text-2xl font-bold text-navy mt-12 mb-4">13. Contact Us</h2>
          <address className="not-italic text-slate-600 leading-relaxed">
            <strong className="text-navy">M A Galaxy Int UK Ltd</strong><br />
            62 New Road, Newhall, Swadlincote, Derby, DE11 0TH, United Kingdom<br />
            Email: <a href="mailto:info@magalaxyintl.com" className="text-gold hover:underline">info@magalaxyintl.com</a><br />
            Phone: <a href="tel:+447379339449" className="text-gold hover:underline"><span dir="ltr">+44 7379 339 449</span></a>
          </address>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
